import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Handshake, Edit, Trash2, Eye, Plus, Globe } from 'lucide-react';
import PageHeader from '../../components/admin/PageHeader';
import DataTable from '../../components/admin/DataTable';
import LoadingState from '../../components/admin/LoadingState';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import toast from 'react-hot-toast';

const Partners: React.FC = () => {
  const [partners, setPartners] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<{isOpen: boolean, partner: any}>({
    isOpen: false,
    partner: null
  });
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Gestion des Partenaires | Admin';
    loadPartners();
  }, []);

  const loadPartners = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('partners')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPartners(data || []);
    } catch (error) {
      console.error('Error loading partners:', error);
      toast.error('Erreur lors du chargement des partenaires');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (partner: any) => {
    navigate(`/admin/partners/edit/${partner.id}`);
  };

  const handleView = (partner: any) => {
    if (partner.website) {
      window.open(partner.website, '_blank');
    } else {
      toast.info(`Partenaire: ${partner.name}`);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm.partner) return;
    
    try {
      const { error } = await supabase
        .from('partners')
        .delete()
        .eq('id', deleteConfirm.partner.id);

      if (error) throw error;
      
      setPartners(partners.filter(p => p.id !== deleteConfirm.partner.id));
      toast.success('Partenaire supprimé avec succès');
    } catch (error) {
      console.error('Error deleting partner:', error);
      toast.error('Erreur lors de la suppression du partenaire');
    } finally {
      setDeleteConfirm({ isOpen: false, partner: null });
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Nom',
      render: (value: string, item: any) => (
        <div className="flex items-center">
          <div className="w-10 h-10 flex-shrink-0 mr-3">
            {item.logo ? (
              <img 
                src={item.logo} 
                alt={value}
                className="w-10 h-10 rounded-md object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'https://via.placeholder.com/40';
                }}
              />
            ) : (
              <div className="w-10 h-10 rounded-md bg-purple-100 flex items-center justify-center">
                <Handshake size={20} className="text-purple-600" />
              </div>
            )}
          </div>
          <div className="font-medium text-gray-900">{value}</div>
        </div>
      ),
      sortable: true
    },
    {
      key: 'website',
      header: 'Site Web',
      render: (value: string) => value ? (
        <a 
          href={value} 
          target="_blank" 
          rel="noopener noreferrer"
          className="flex items-center text-primary-600 hover:text-primary-700"
        >
          <Globe size={16} className="mr-1" />
          {value.replace(/^https?:\/\/(www\.)?/, '')}
        </a>
      ) : (
        <span className="text-gray-400">Non renseigné</span>
      ),
      width: 'w-64'
    },
    {
      key: 'created_at',
      header: 'Ajouté le',
      render: (value: string) => new Date(value).toLocaleDateString('fr-FR'),
      sortable: true,
      width: 'w-32'
    }
  ];

  return (
    <div className="px-6">
      <PageHeader
        title="Partenaires"
        description="Gérez les partenaires de votre entreprise"
        actionLink="/admin/partners/new"
        actionLabel="Ajouter un partenaire"
        actionIcon={<Plus size={16} />}
      />
      
      {loading ? (
        <LoadingState type="table" />
      ) : (
        <DataTable
          columns={columns}
          data={partners}
          onEdit={handleEdit}
          onDelete={(partner) => setDeleteConfirm({ isOpen: true, partner })}
          onView={handleView}
          searchPlaceholder="Rechercher un partenaire..."
          searchKey="name"
          emptyTitle="Aucun partenaire"
          emptyDescription="Vous n'avez pas encore ajouté de partenaire."
          emptyActionLabel="Ajouter un partenaire"
          onEmptyAction={() => navigate('/admin/partners/new')}
        />
      )}
      
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Supprimer le partenaire"
        message={`Êtes-vous sûr de vouloir supprimer le partenaire "${deleteConfirm.partner?.name}" ? Cette action est irréversible.`}
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, partner: null })}
        type="danger"
      />
    </div>
  );
};

export default Partners;